import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import {
  ArsConsultNoteImport,
  ArsConsultNoteImportFilters
} from 'app/core/models/ars-consult-note-import.model';
import { ArsConsultNoteImportsService } from 'app/core/services/ars-consult-note-imports.service';
import { ImportNavComponent } from '../../../shared/components/import-nav/import-nav.component';
import { PaginationHeaderComponent } from '../../../shared/components/pagination-header/pagination-header.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { ReplacePipe } from '../../../shared/pipes/replace.pipe';
import { PatientNameComponent } from '../../../shared/components/patient-name/patient-name.component';
import { PrescriberNameComponent } from '../../../shared/components/prescriber-name/prescriber-name.component';
import { ClientSelectComponent } from '../../../shared/components/client-select/client-select.component';

const ACTIVE_STATUSES = ['imported', 'ocr_processing', 'llm_processing', 'resolve_details'];
const REFRESH_INTERVAL_MS = 5000;

@Component({
  selector: 'app-capture-admin-import-ars-consult-notes-history',
  templateUrl: './capture-admin-import-ars-consult-notes-history.component.html',
  imports: [
    ImportNavComponent,
    FormsModule,
    RouterLink,
    PaginationHeaderComponent,
    PaginationComponent,
    MatProgressSpinner,
    MatSlideToggle,
    DatePipe,
    TitleCasePipe,
    ReplacePipe,
    PatientNameComponent,
    PrescriberNameComponent,
    ClientSelectComponent
  ]
})
export class CaptureAdminImportArsConsultNotesHistoryComponent implements OnInit, OnDestroy {
  private arsConsultNoteImportsService = inject(ArsConsultNoteImportsService);

  loading = false;
  arsConsultNoteImports: ArsConsultNoteImport[] = [];
  filters = new ArsConsultNoteImportFilters();
  paging: LimitOffsetPaging;
  nextCheckTimeout = null;

  ngOnInit() {
    this.paging = LimitOffsetPaging.empty;
    this.loadImports(this.paging.currentPage);
  }

  ngOnDestroy() {
    clearTimeout(this.nextCheckTimeout);
  }

  onPageSizeChange(size: number) {
    this.paging.pageSize = size;
    this.loadImports(this.paging.currentPage);
  }

  onPageChange(page: number) {
    this.loadImports(page);
  }

  onFilterChange() {
    this.loadImports(1);
  }

  onResetFiltersClick() {
    this.filters = new ArsConsultNoteImportFilters();
    this.loadImports(1);
  }

  get patientNameFiltered(): boolean {
    return !!(this.filters.patientFirstName?.trim() || this.filters.patientLastName?.trim());
  }

  isActive(arsConsultNoteImport: ArsConsultNoteImport): boolean {
    return !arsConsultNoteImport.requiresIntervention && ACTIVE_STATUSES.includes(arsConsultNoteImport.status);
  }

  private loadImports(page: number, loading = true) {
    clearTimeout(this.nextCheckTimeout);
    this.loading = loading;
    const paging = { page, pageSize: this.paging.pageSize };

    this.arsConsultNoteImportsService.getList(paging, this.filters).subscribe(result => {
      this.arsConsultNoteImports = result.arsConsultNoteImports;
      this.paging = result.meta.paging;
      this.loading = false;

      // keep statuses current while imports are still being processed, except during a patient name search, where
      // each refresh would be audited as another search
      const anyActive = this.arsConsultNoteImports.some(arsConsultNoteImport => this.isActive(arsConsultNoteImport));

      if (anyActive && !this.patientNameFiltered) {
        this.nextCheckTimeout = setTimeout(() => this.loadImports(this.paging.currentPage, false), REFRESH_INTERVAL_MS);
      }
    });
  }
}
